import { inject, Injectable, isDevMode, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as fromShared from '@shared';
import { Socket, SocketIoConfig } from 'ngx-socket-io';
import { debounceTime, merge, Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import * as fromGenerated from '../_generated';
import * as fromAuth from '../auth';
import { AppActions } from '../store/app.actions';
import { AppState } from '../store/app.reducer';
import * as fromAssistants from './assistants';
import { ChatWithMessagesActions } from './store/chats.actions';
import { ChatsState } from './store/chats.reducer';
import { TeammatesActions } from './teammates/store';

export const chatsSocketConfig: SocketIoConfig = {
  url: `${environment.appApiUrl}/chats`,
  options: {
    autoConnect: false,
    reconnection: true,
    closeOnBeforeunload: true,
    withCredentials: true,
    protocols: ['polling', 'websocket'],
    upgrade: true,
  },
};

/** Coalesce bursts of access-change events (and the initial connect) into one reload. */
const ACCESS_RELOAD_DEBOUNCE_MS = 300;

@Injectable()
export class ChatsSocket {
  private readonly store = inject<Store<ChatsState>>(Store);
  private readonly appStore = inject<Store<AppState>>(Store);
  private readonly translateService = inject(TranslateService);
  private readonly socket = inject<Socket>(Socket);
  private readonly ngZone = inject(NgZone);

  /**
   * Observable of a socket event, built on `socket.on()` rather than
   * `socket.fromEvent()`.
   *
   * `fromEvent()`'s listener calls `appRef.tick()` on every delivery
   * (ngx-socket-io 4.10.0, `ngx-socket-io.mjs:118-122` — the only `tick()` call
   * site in the package). Because this class registers its listeners and calls
   * `connect()` inside `runOutsideAngular`, that tick ran change detection at
   * `NgZone._nesting === 0`; `runEffectsInView` then re-entered the Angular zone
   * per dirty effect, and leaving it fired `onMicrotaskEmpty` mid-tick, whose
   * subscriber called `ApplicationRef._tick()` inside the tick already running —
   * `NG0101: ApplicationRef.tick is called recursively`.
   *
   * `socket.on()` is a plain passthrough with no tick
   * (`ngx-socket-io.mjs:77-80`). Change detection is scheduled by the explicit
   * `ngZone.run(...)` wrappers around each dispatch below.
   */
  private onEvent<T>(eventName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      const listener = (data: T) => subscriber.next(data);
      this.socket.on(eventName, listener);
      return () => this.socket.removeListener(eventName, listener);
    });
  }

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      this.onEvent<fromShared.VersionCheckEventPayload>(
        fromShared.ChatsGatewayEventsType.VersionCheck,
      )
        .pipe(
          takeUntilDestroyed(),
          tap(({ apiReleaseVersion }) => {
            this.ngZone.run(() => {
              this.appStore.dispatch(
                AppActions.setApiReleaseVersion({
                  apiReleaseVersion,
                }),
              );
            });
          }),
        )
        .subscribe();

      this.onEvent<{
        type: string;
        chatWithMessages: fromGenerated.ChatWithMessagesView;
      }>(fromShared.ChatsGatewayEventsType.ChatMessageAnswered)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatWithMessages }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess(
                  {
                    chatWithMessages,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      /**
       * SB-3651. The first chat listener with a COMPILE-CHECKED payload:
       * `ChatMessageStreamPayload` is primitives-only, so it can live in
       * `libs/shared` and both halves of the contract are the same type. The
       * hand-typed structural literals above are not a style to copy — they
       * exist because their payloads reference `apps/hs/api` view types.
       */
      this.onEvent<fromShared.ChatMessageStreamPayload>(
        fromShared.ChatsGatewayEventsType.ChatMessageStream,
      )
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatId, chatMessageId, event }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedChatStreamEventFromSocket({
                  chatId,
                  chatMessageId,
                  event,
                }),
              );
            });
          }),
        )
        .subscribe();

      this.onEvent<{
        type: string;
        chatWithMessages: fromGenerated.ChatWithMessagesView;
        errorCode: string;
      }>(fromShared.ChatsGatewayEventsType.ChatMessageError)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatWithMessages, errorCode }) => {
            console.error(`Error received from socket, code "${errorCode}"`);

            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure(
                  {
                    chatWithMessages,
                    errorCode,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.onEvent<{
        type: string;
        uuid: fromGenerated.AssistantView['uuid'];
        status: fromGenerated.AssistantView['status'];
        documents: fromGenerated.AssistantView['documents'];
      }>(fromShared.ChatsGatewayEventsType.AssistantsStatusUpdated)
        .pipe(
          takeUntilDestroyed(),
          tap<{
            uuid: fromGenerated.AssistantView['uuid'];
            status: fromGenerated.AssistantView['status'];
            documents: fromGenerated.AssistantView['documents'];
          }>(({ uuid, status, documents }) => {
            // Wrapped like its siblings: this handler used to rely on
            // `fromEvent()`'s implicit `appRef.tick()` to render, which no longer
            // happens now that listeners come from `onEvent()`.
            this.ngZone.run(() => {
              this.store.dispatch(
                fromAssistants.AssistantActions.receivedAssistantStatusUpdatedFromSocket(
                  {
                    uuid,
                    status,
                    documents,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.onEvent<{
        type: string;
        userId: string;
        chatUuid: string;
        chatMessageId: number;
        documentCitations: fromGenerated.DocumentCitationView[];
      }>(fromShared.ChatsGatewayEventsType.ChatMessageDocumentCitationsAdded)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatUuid, chatMessageId, documentCitations }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedDocumentCitationsFromSocketSuccess(
                  {
                    chatUuid,
                    chatMessageId,
                    documentCitations,
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      this.onEvent<{
        type: string;
        userId: string;
        chatUuid: string;
        documentId: string;
        documentStatus: fromGenerated.DocumentView['status'];
        failureReason?: fromShared.IngestionFailureReason;
      }>(fromShared.ChatsGatewayEventsType.ChatDocumentsStatusUpdated)
        .pipe(
          takeUntilDestroyed(),
          tap(({ chatUuid, documentId, documentStatus, failureReason }) => {
            this.ngZone.run(() => {
              this.store.dispatch(
                ChatWithMessagesActions.receivedDocumentStatusUpdatedFromSocketSuccess(
                  {
                    chatUuid,
                    documentId,
                    documentStatus,
                    // Spread, not assigned: `exactOptionalPropertyTypes` rejects
                    // an explicit `undefined`, and only `failed` carries a reason.
                    ...(failureReason && { failureReason }),
                  },
                ),
              );
            });
          }),
        )
        .subscribe();

      // A teammate or skill was shared with (or un-shared from) this user, or
      // the socket (re)connected after possibly missing such an event. Refresh
      // BOTH stores: the teammate grid is a client-side join of the teammates
      // list and the assistants store (teammates.reducer
      // `selectAllWithAssistants`), so reloading only one leaves the other stale
      // — e.g. a freshly-granted teammate stays hidden until its skills load.
      // Debounced to coalesce bursts (bulk membership edits, org-wide
      // broadcasts, the initial connect) into a single pair of refetches.
      const reloadAccessibleResources = () =>
        this.ngZone.run(() => {
          this.store.dispatch(
            TeammatesActions.loadTeammates({
              locale: this.translateService.getCurrentLang() as 'en' | 'fr',
            }),
          );
          this.store.dispatch(
            fromAssistants.AssistantsActions.loadAssistants(),
          );
        });

      merge(
        this.onEvent<fromShared.TeammatesUpdatedEventPayload>(
          fromShared.ChatsGatewayEventsType.TeammatesUpdated,
        ),
        this.onEvent<fromShared.SkillsUpdatedEventPayload>(
          fromShared.ChatsGatewayEventsType.SkillsUpdated,
        ),
        // Re-sync on (re)connect — events missed while disconnected (sleep,
        // network blip, deploy, a background tab) are otherwise lost until a
        // manual reload.
        this.onEvent('connect'),
      )
        .pipe(
          takeUntilDestroyed(),
          debounceTime(ACCESS_RELOAD_DEBOUNCE_MS),
          tap(reloadAccessibleResources),
        )
        .subscribe();

      this.onEvent<fromShared.ChatsGatewayErrorFrame>('error')
        .pipe(
          takeUntilDestroyed(),
          tap((error) => {
            console.error(`${error.name}: ${error.message}`);
            /**
             * Matched on the exception NAME and its cause, not on a sentence.
             * The previous check read `status === 401` and a fragment of the
             * gateway's prose — neither of which ever arrives: `ApiException`
             * pins `message` to the wrapper text and defaults `status` to 500,
             * so this branch had never fired. SB-3650.
             */
            if (
              error.name === fromShared.CHATS_GATEWAY_HANDLE_CONNECTION_ERROR &&
              fromShared.CHATS_GATEWAY_RECOVERABLE_CAUSE_NAMES.some(
                (name) => name === error.cause?.name,
              )
            ) {
              console.warn('Refreshing tokens');
              this.ngZone.run(() => {
                this.store.dispatch(fromAuth.AuthActions.refreshTokens());
              });
            }
          }),
        )
        .subscribe();

      if (isDevMode()) {
        // SB-3651: one frame per token would drown every other event in this
        // log. Widened to `string` because `onAny` hands one over.
        const streamEvent: string =
          fromShared.ChatsGatewayEventsType.ChatMessageStream;

        this.socket.onAny((event: string) => {
          if (event !== streamEvent) {
            // eslint-disable-next-line no-console
            console.info('[ChatsSocket] %s', event);
          }
        });
      }

      this.socket.on('error', console.error);
      this.socket.connect();
    });
  }

  reconnect() {
    this.socket.disconnect();
    this.socket.connect();
  }
}
